import { Component, OnDestroy, computed, inject, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { Subscription, combineLatest, distinctUntilChanged } from 'rxjs';

import { WeekRangeSelectionStrategy } from '@shared/week-selection-strategy';

export interface WeekRange {
  start: Date;
  end: Date;
}

@Component({
  selector: 'app-week-range-selector',
  templateUrl: './week-range-selector.component.html',
  imports: [
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: WeekRangeSelectionStrategy,
    },
  ],
})
export class WeekRangeSelectorComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private _sub = new Subscription();

  startDate = input<Date | DateTime | null>(null);
  endDate = input<Date | DateTime | null>(null);
  clearable = input<boolean>(false);

  weekChanged = output<WeekRange>();
  cleared = output<void>();

  protected pickerForm: FormGroup<{
    start: FormControl<Date | null>;
    end: FormControl<Date | null>;
  }> = this.fb.group({
    start: this.fb.control<Date | null>(null),
    end: this.fb.control<Date | null>(null),
  });

  // True when a valid start date is present — drives the clear button visibility.
  protected hasValue = computed(() => {
    const s = this.startDate();
    if (!s) return false;
    // An invalid Luxon DateTime is treated as no value.
    return s instanceof Date ? true : DateTime.isDateTime(s) && s.isValid;
  });

  constructor() {
    // Sync incoming input signals into the internal picker form controls.
    // Using toObservable + combineLatest instead of effect() to avoid
    // ExpressionChangedAfterItHasBeenChecked in dev mode when the parent
    // updates signal values inside a (weekChanged) event handler.
    this._sub.add(
      combineLatest([toObservable(this.startDate), toObservable(this.endDate)])
        .pipe(
          distinctUntilChanged(
            ([prevStart, prevEnd], [currStart, currEnd]) =>
              this._toDate(prevStart)?.getTime() === this._toDate(currStart)?.getTime() &&
              this._toDate(prevEnd)?.getTime() === this._toDate(currEnd)?.getTime(),
          ),
        )
        .subscribe(([start, end]) => {
          this.pickerForm.setValue(
            { start: this._toDate(start), end: this._toDate(end) },
            { emitEvent: false },
          );
        }),
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  // Called when the picker panel closes — fires exactly once per selection,
  // regardless of whether the strategy sets start and end simultaneously.
  // With LuxonDateAdapter the form holds DateTime instances even though the
  // FormControl is typed `Date | null`; normalise through `_toDate` so the
  // emitted `WeekRange` always contains real `Date`s, matching the contract.
  protected onPickerClosed(): void {
    const start = this._toDate(this.pickerForm.value.start);
    const end = this._toDate(this.pickerForm.value.end);
    if (start && end) {
      this.weekChanged.emit({ start, end });
    }
  }

  protected navigateWeek(direction: -1 | 1): void {
    const current = this.startDate();
    if (!current) return;
    // Use Luxon for DST-safe day arithmetic — native setDate can drift by an
    // hour across DST boundaries even though the day-of-month stays correct.
    const currentDT = DateTime.isDateTime(current) ? current : DateTime.fromJSDate(current);
    const newStart = currentDT.plus({ weeks: direction });
    const newEnd = newStart.plus({ days: 6 });
    this.weekChanged.emit({ start: newStart.toJSDate(), end: newEnd.toJSDate() });
  }

  protected onClear(): void {
    this.cleared.emit();
  }

  private _toDate(v: Date | DateTime | null | undefined): Date | null {
    if (!v) return null;
    return DateTime.isDateTime(v) ? v.toJSDate() : v;
  }
}
